import { env } from '@/libs/env'

export const LatestVersionKey = 'latestVersion'

const reloadRetryKey = 'versionReloadRetry'
/**
 * 配信の切り替え直後などはリロードしても新しい版が返らないことがあるため、間隔を空けて再挑戦する。
 * セルフCI端末はタブを閉じずに何週間も動き続けるので、回数で諦めずに繰り返す。
 */
const firstRetryIntervalMs = 60 * 1000
const retryIntervalMs = 15 * 60 * 1000

type ReloadRetry = { count: number; reloadedAt: number }

const parseReloadRetry = (stored: string | null): ReloadRetry | undefined => {
  if (!stored) return undefined
  try {
    const value = JSON.parse(stored)
    return typeof value?.count === 'number' && typeof value?.reloadedAt === 'number' ? value : undefined
  } catch {
    return undefined
  }
}

/**
 * @returns 今回リロードする場合は保存する試行状況。前回のリロードから間隔が空いていない場合は null
 */
export const nextReloadRetry = (stored: string | null, now: number): ReloadRetry | null => {
  const retry = parseReloadRetry(stored)
  if (!retry) {
    return { count: 1, reloadedAt: now }
  }
  const elapsed = now - retry.reloadedAt
  const interval = retry.count === 1 ? firstRetryIntervalMs : retryIntervalMs
  // 端末の時計が戻った場合に待ち続けないよう、経過時間が負なら間隔を満たしたものとして扱う
  return elapsed < 0 || elapsed >= interval ? { count: retry.count + 1, reloadedAt: now } : null
}

/**
 * 本番の API はタグ部分だけを返す（v1.2.3-45-gabc1234 -> v1.2.3）ため、タグ部分の一致も同じバージョンとみなす。
 * タグの付いていないコミットから配信された場合に、不一致のままリロードを繰り返さないようにする。
 */
export const isSameVersion = (appVersion: string, latestVersion: string): boolean =>
  appVersion === latestVersion || appVersion.split('-')[0] === latestVersion

/**
 * @returns true: fire reload
 */
export const reloadIfVersionHasChanged = (): boolean => {
  const thisVersion = env('APP_TAG') as string
  const latestVersion = sessionStorage.getItem(LatestVersionKey)
  // 最新バージョンは起動直後・リロード直後（API の応答前）や API が取得できなかったときに、
  // 自身のバージョンはビルドに埋め込まれていないときに分からない。比較できないため不一致として扱わない
  if (!thisVersion || !latestVersion) {
    return false
  }
  if (isSameVersion(thisVersion, latestVersion)) {
    sessionStorage.removeItem(reloadRetryKey)
    return false
  }

  try {
    const retry = nextReloadRetry(sessionStorage.getItem(reloadRetryKey), Date.now())
    if (!retry) {
      return false
    }
    sessionStorage.setItem(reloadRetryKey, JSON.stringify(retry))
  } catch (e) {
    // 前回のリロード時刻を残せないままリロードすると間隔を空けられず再読み込みを繰り返すため、更新を見送る
    console.error(e)
    return false
  }

  sessionStorage.removeItem(LatestVersionKey)
  window.location.reload()
  return true
}
